/*
 * Share-card generator + share helper.
 * Draws a fun branded result card on a <canvas> (no extra deps) and shares it
 * via the Web Share API when available, with graceful fallbacks:
 *   1) Share image file (navigator.canShare files)
 *   2) Share text + URL (navigator.share)
 *   3) Download the image + copy the text to clipboard
 */

export const formatTime = (ms) => (ms == null ? "--" : `${(ms / 1000).toFixed(2)}s`);

function drawExcavator(ctx, cx, cy, s) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = "#0e1217";
  ctx.lineWidth = 6;
  ctx.fillStyle = "#FFD23F";

  const rr = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  };

  // body
  rr(-90, 10, 150, 46, 12);
  // cab
  rr(-60, -28, 64, 44, 8);
  // boom arm
  ctx.beginPath();
  ctx.moveTo(10, -10);
  ctx.lineTo(120, -62);
  ctx.lineWidth = 16;
  ctx.strokeStyle = "#FFD23F";
  ctx.stroke();
  ctx.strokeStyle = "#0e1217";
  ctx.lineWidth = 6;
  ctx.stroke();
  // bucket
  ctx.beginPath();
  ctx.moveTo(112, -74);
  ctx.lineTo(146, -62);
  ctx.lineTo(130, -34);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // wheels
  ctx.fillStyle = "#0e1217";
  [-55, 25].forEach((wx) => {
    ctx.beginPath();
    ctx.arc(wx, 62, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#FFD23F";
    ctx.beginPath();
    ctx.arc(wx, 62, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#0e1217";
  });
  ctx.restore();
}

function drawHazardBand(ctx, y, w, h) {
  ctx.save();
  ctx.fillStyle = "#FFD23F";
  ctx.fillRect(0, y, w, h);
  ctx.fillStyle = "#0e1217";
  const stripe = 48;
  for (let x = -h; x < w; x += stripe * 2) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + stripe, y);
    ctx.lineTo(x + stripe - h, y + h);
    ctx.lineTo(x - h, y + h);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

export async function generateCardBlob(timeText) {
  const W = 1080;
  const H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // make sure brand fonts are ready for canvas
  if (document.fonts && document.fonts.load) {
    try {
      await Promise.all([
        document.fonts.load('700 150px "Bebas Neue"'),
        document.fonts.load('700 40px "Manrope"'),
      ]);
    } catch (e) {
      /* ignore */
    }
  }

  // background
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#131922");
  bg.addColorStop(1, "#0a0d11");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // amber glow
  const glow = ctx.createRadialGradient(W / 2, 120, 60, W / 2, 120, 620);
  glow.addColorStop(0, "rgba(255,210,63,0.22)");
  glow.addColorStop(1, "rgba(255,210,63,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // hazard bands
  drawHazardBand(ctx, 0, W, 70);
  drawHazardBand(ctx, H - 70, W, 70);

  // excavator
  drawExcavator(ctx, W / 2, 285, 1.55);

  ctx.textAlign = "center";

  // headline
  ctx.fillStyle = "#FFD23F";
  ctx.font = '700 78px "Bebas Neue", sans-serif';
  ctx.fillText("I REACHED", W / 2, 560);

  ctx.fillStyle = "#ffffff";
  ctx.font = '700 118px "Bebas Neue", sans-serif';
  ctx.fillText("COTTAGE 837", W / 2, 672);

  // time line
  ctx.fillStyle = "#FF7A18";
  ctx.font = '700 150px "Bebas Neue", sans-serif';
  ctx.fillText(`IN ${timeText}!`, W / 2, 820);

  // subtitle
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.font = '700 40px "Manrope", sans-serif';
  ctx.fillText("ADVIT TURNS 8  •  24 OCT  •  5–8 PM IST", W / 2, 900);

  // footer brand
  ctx.fillStyle = "#FFD23F";
  ctx.font = '700 40px "Bebas Neue", sans-serif';
  ctx.fillText("ADVIT'S EXCAVATOR DASH", W / 2, 975);

  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/png", 0.92);
  });
}

export async function shareResult(timeMs) {
  const timeText = formatTime(timeMs);
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const text = `I reached Cottage 837 in ${timeText}! 🚜 Come celebrate Advit's 8th birthday — 24 Oct, 5–8 PM IST.`;

  let blob = null;
  try {
    blob = await generateCardBlob(timeText);
  } catch (e) {
    blob = null;
  }

  // 1) native share with image file
  if (blob && typeof navigator !== "undefined" && navigator.canShare) {
    try {
      const file = new File([blob], "cottage-837-result.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "Advit's Excavator Dash" });
        return { ok: true, method: "image" };
      }
    } catch (e) {
      if (e && e.name === "AbortError") return { ok: false, method: "cancel" };
    }
  }

  // 2) native share with text + url
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title: "Advit's Excavator Dash", text, url });
      return { ok: true, method: "text" };
    } catch (e) {
      if (e && e.name === "AbortError") return { ok: false, method: "cancel" };
    }
  }

  // 3) fallback: download image + copy text
  if (blob) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cottage-837-result.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
  } catch (e) {
    /* ignore */
  }
  return { ok: true, method: "fallback" };
}
